var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export var ModelNumber;
(function (ModelNumber) {
    ModelNumber["SB6000"] = "-LvGbyjZg1CEXffX_OD9";
    ModelNumber["GX"] = "-NzDem2jMvEXIaZK3SPX";
})(ModelNumber || (ModelNumber = {}));
export class SmartBoard {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        // other class properties and constructor
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        this.modelNumber = ModelNumber.SB6000;
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const fwcommand = this.modelNumber === ModelNumber.SB6000 ? 'fwverscr' : 'fwversion';
                const match = yield this.socket.send(`get ${fwcommand}`, new RegExp(`${fwcommand}=(.+)|invalidcmd`, 'i'), tags);
                return match[1] ? match[1] : 'unknown';
            }),
            powerOffRestrictions: ['get'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`get powerstate`, /powerstate=(.+)|invalidcmd/i, tags);
                return match[1] ? match[1].toLowerCase() === 'on' : this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // Setting power to "powersave" seems to put the displays in a low power mode which
                // turns off the RS232 interface after 30 minutes or so. Setting power to "ready" seems
                // to put the display into a higher power mode but still off.
                const offState = this.modelNumber === ModelNumber.SB6000 ? 'standby' : 'ready';
                const match = yield this.socket.send(`set powerstate=${power ? 'on' : offState}`, /powerstate=(.+)|invalidcmd/i, tags);
                const powerState = match[1] ? match[1].toLowerCase() === 'on' : this.state.power.value;
                return powerState;
            }),
            asyncHandlers: {
                name: 'power',
                regExp: /#powerstate=(powersave|ready|on)/i,
                callback: (match) => {
                    const powerState = match[1]
                        ? match[1].toLowerCase() === 'on'
                        : this.state.power.value;
                    this.state.power.update(powerState);
                },
            },
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`get input`, /input=(.+)|invalidcmd/i, tags);
                return match[1] ? match[1] : this.state.videoInput.value;
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`set input=${code}`, /input=(.+)|invalidcmd/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                return match[1] ? match[1] : this.state.videoInput.value;
            }),
            asyncHandlers: {
                name: 'videoInput',
                regExp: /#input=(\S+)/i,
                callback: (match) => {
                    this.state.videoInput.update(match[1]);
                },
            },
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`get volume`, /volume=(\d+)|invalidcmd/i, tags);
                return match[1] ? parseInt(match[1], 10) : this.state.volume.value;
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`set volume=${volume}`, /volume=(\d+)|invalidcmd/i, tags);
                return match[1] ? parseInt(match[1], 10) : this.state.volume.value;
            }),
            range: { max: 100, min: 0, round: 1 },
            asyncHandlers: {
                name: 'volume',
                regExp: /#volume=(\d+)/i,
                callback: (match) => {
                    this.state.volume.update(parseInt(match[1], 10));
                },
            },
            powerOffRestrictions: ['get', 'set'],
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`get mute`, /mute=(.+)|invalidcmd/i, tags);
                return match[1] ? match[1].toLowerCase() === 'on' : this.state.volumeMute.value;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`set mute=${mute ? 'on' : 'off'}`, /mute=(.+)|invalidcmd/i, tags);
                return match[1] ? match[1].toLowerCase() === 'on' : this.state.volumeMute.value;
            }),
            asyncHandlers: {
                name: 'mute',
                regExp: /#mute=(on|off)/i,
                callback: (match) => {
                    this.state.mute.update(match[1].toLowerCase() === 'on');
                },
            },
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            {
                intervalMs: 6000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.socket.send(`get powerstate`, /powerstate|invalidcmd/i, ['poll']);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.modelNumber = (_a = config === null || config === void 0 ? void 0 : config.modelNumber) !== null && _a !== void 0 ? _a : ModelNumber.SB6000;
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: `get powerstate`,
                    next: /powerstate|invalidcmd/i,
                },
            ], credentials);
        });
    }
}
// static properties
SmartBoard.driverType = 'socketDriver';
SmartBoard.key = '-LvGbvQDnePZestGJSyS';
SmartBoard.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r'],
    },
    sendTerminator: '\r',
};
SmartBoard.socketOptions = [
    Object.assign(Object.assign({}, SmartBoard.baseSocketOptions), { port: 4999 }),
];
